// Vue Router setup. Dashboard surfaces: Workspace (quota/alerts),
// New audit, Runs, Monitors (+ per-monitor detail), Flows, API keys.
//
// The /login and /auth/callback routes live OUTSIDE the dashboard
// layout (App.vue renders a minimal Login/AuthCallback page, not
// the sidebar+main shell). Routes are matched by Vue Router; the
// layout is chosen inside each view.

function billingRedirect(outcome) {
  return (to) => ({
    path: '/billing',
    query: { ...to.query, billing: outcome },
    hash: to.hash,
  })
}

export function emailVerificationRedirectUrl(to) {
  if (to.query.action !== 'verify') return null
  const token = typeof to.query.token === 'string' ? to.query.token : ''
  if (!token) return null
  return `/api/auth/email/verify?token=${encodeURIComponent(token)}`
}

export const routes = [
  {
    path: '/dashboard/:pathMatch(.*)*',
    redirect: (to) => {
      const rest = Array.isArray(to.params.pathMatch)
        ? to.params.pathMatch.join('/')
        : to.params.pathMatch || ''
      return {
        path: rest ? `/${rest}` : '/',
        query: to.query,
        hash: to.hash,
      }
    },
  },
  { path: '/login', component: () => import('./views/Login.vue') },
  {
    path: '/auth/callback',
    beforeEnter: (to) => {
      const redirectUrl = emailVerificationRedirectUrl(to)
      if (!redirectUrl) return true
      window.location.href = redirectUrl
      return false
    },
    component: () => import('./views/AuthCallback.vue'),
  },
  {
    path: '/auth/callback/:pathMatch(.*)*',
    redirect: (to) => ({
      path: '/auth/callback',
      query: to.query,
      hash: to.hash,
    }),
  },
  { path: '/reset/request', component: () => import('./views/EmailResetRequest.vue') },
  { path: '/reset/confirm', component: () => import('./views/EmailResetConfirm.vue') },
  { path: '/billing/success', redirect: billingRedirect('checkout_success') },
  { path: '/billing/cancel', redirect: billingRedirect('checkout_cancelled') },
  { path: '/', component: () => import('./views/Workspace.vue') },
  { path: '/billing', component: () => import('./views/Billing.vue') },
  { path: '/support', component: () => import('./views/Support.vue') },
  { path: '/runs', component: () => import('./views/Runs.vue') },
  { path: '/runs/:id', component: () => import('./views/RunDetail.vue') },
  { path: '/monitors', component: () => import('./views/Monitors.vue') },
  { path: '/monitors/:id', component: () => import('./views/MonitorDetail.vue') },
  { path: '/flows', component: () => import('./views/Flows.vue') },
  { path: '/flows/import-gremlin', component: () => import('./views/ImportGremlinFlow.vue') },
  { path: '/flows/:id', component: () => import('./views/FlowDetail.vue') },
  { path: '/new', component: () => import('./views/NewAudit.vue') },
  { path: '/gremlin', component: () => import('./views/Gremlin.vue') },
  { path: '/api-keys', component: () => import('./views/ApiKeys.vue') },
  { path: '/mcp', component: () => import('./views/McpServer.vue') },
  // Catch-all LAST: unknown paths get a real 404 view instead of a
  // silently empty main area.
  { path: '/:pathMatch(.*)*', component: () => import('./views/NotFound.vue') },
  // /for/marketers/ showcase views — DEV-gated. Vite tree-shakes
  // this block from the prod bundle (import.meta.env.DEV is statically
  // replaced at build time). The views themselves import from
  // src/mocks/marketers.js, which has no runtime side effects, so a
  // prod build doesn't ship marketing-themed fixtures.
  ...(import.meta.env.DEV
    ? [
        { path: '/showcase/audit-board', component: () => import('./views/showcase/AuditBoard.vue') },
        { path: '/showcase/tracking-log', component: () => import('./views/showcase/TrackingLog.vue') },
        { path: '/showcase/audit-report', component: () => import('./views/showcase/AuditReport.vue') },
        { path: '/showcase/monitor-marketing', component: () => import('./views/showcase/MonitorMarketing.vue') },
      ]
    : []),
]
